import {
  makeStyles,
  Typography,
  Box,
  FormControlLabel,
  Button,
  CircularProgress,
  RadioGroup,
  Radio,
} from '@material-ui/core';
import { useFormik } from 'formik';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { saveUnsubscribeFeedback } from 'services/notification';
import imageLogo from 'assets/img/logo.png';
import { object, string } from 'yup';
import { validate } from 'uuid';

const useStyles = makeStyles((theme) => ({
  container: {
    background: theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: 'calc(100vh - 160px)',
    '& p, b': {
      color: theme.palette.common.black,
    },
  },
  header: {
    width: '100%',
    height: 80,
    borderBottom: '1px solid rgba(0,0,0, 0.16)',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 4),
  },
  footer: {
    padding: theme.spacing(0, 4),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '100%',
    height: 80,
    borderTop: '1px solid rgba(0,0,0, 0.16)',
    '& button': {
      height: 45,
      background: theme.palette.primary.main,
      textTransform: 'capitalize',
    },
  },
  options: {
    display: 'flex',
    margin: theme.spacing(4, 0),
    flexDirection: 'column',
  },
}));

const validationSchema = object({
  reason: string().required('Você precisa selecionar um motivo'),
});

const Unsubscribe = () => {
  const [saved, setSaved] = useState(false);
  const { trackId } = useParams<{ trackId: string }>();
  const classes = useStyles();
  const reasons = useMemo(
    () => [
      'Não quero mais receber estes e-mails.',
      'Eu nunca me inscrevi para essa lista de e-mails.',
      'O assunto não é do meu interesse.',
      'Estou recebendo muitos e-mails dessa lista.',
      'Outro motivo.',
    ],
    []
  );
  const formik = useFormik({
    initialValues: {
      reason: '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: ({ reason }) => {
      formik.setSubmitting(true);
      saveUnsubscribeFeedback({
        trackId,
        reason,
      }).finally(() => {
        setSaved(true);
        formik.setSubmitting(false);
      });
    },
  });

  const submit = () => {
    formik.submitForm();
  };

  if (!validate(trackId)) {
    return null;
  }

  if (saved) {
    return (
      <>
        <div className={classes.header}>
          <img src={imageLogo} alt='Nutror logo' height={30} />
        </div>
        <div className={classes.container}>
          <Typography variant='body1' component='b'>
            Obrigado pelo seu feedback
          </Typography>
          <Box marginY={4}>
            <Typography variant='body2'>
              Seu feedback é muito importante para melhorarmos cada vez mais sua experência
            </Typography>
          </Box>
        </div>
        <div className={classes.footer}></div>
      </>
    );
  }

  return (
    <>
      <div className={classes.header}>
        <img src={imageLogo} alt='Nutror logo' height={30} />
      </div>
      <div className={classes.container}>
        <div>
          <Typography variant='body1' component='b'>
            E-mail descadastrado com sucesso!
          </Typography>
          <Box marginY={4}>
            <Typography variant='body2'>
              Seu feedback é muito importante para melhorarmos cada vez mais sua experência
            </Typography>
          </Box>
          <Typography variant='body2' component='b'>
            Por que você está descadastrando da lista de e-mails?
          </Typography>
          <div className={classes.options}>
            <RadioGroup>
              {reasons.map((reason, index) => (
                <FormControlLabel
                  key={index}
                  value={reason}
                  name='reason'
                  control={<Radio color='primary' onClick={formik.handleChange} />}
                  label={reason}
                />
              ))}
            </RadioGroup>
            {formik.errors.reason && (
              <Typography variant='caption' color='error'>
                {formik.errors.reason}
              </Typography>
            )}
          </div>
          <Typography variant='caption'>
            Para voltar para a lista você precisa acessar Configuração de notificações no Nutror e habilitar o envio de
            e-mails.
          </Typography>
        </div>
      </div>
      <div className={classes.footer}>
        <Button variant='contained' color='primary' onClick={submit} disabled={formik.isSubmitting || !formik.isValid}>
          {formik.isSubmitting && (
            <Box marginRight={4} marginTop={2}>
              <CircularProgress size={20} color='inherit' />
            </Box>
          )}
          Enviar resposta
        </Button>
      </div>
    </>
  );
};

export default Unsubscribe;
